import DOMPurify from 'dompurify';
import { forwardRef, useMemo } from 'react';
import { slowCn } from '../../lib/utils';
import { TYPOGRAPHY_ROOT_CLASSNAME, typographyVariants } from './styles';
import type { TypographyComponentAs, TypographyProps, TypographyVariants } from './types';
import { getTypographyStyle } from './utils/getTypographyStyle';

export const COMPONENTS: Record<TypographyVariants, TypographyComponentAs> = {
  'display-2xl': 'h1',
  'display-xl': 'h2',
  'display-lg': 'h3',
  'display-md': 'h4',
  'display-sm': 'h5',
  'display-xs': 'h6',
  'text-xl': 'p',
  'text-lg': 'p',
  'text-md': 'p',
  'text-sm': 'p',
  'text-xsm': 'p',
  'text-xs': 'p',
  'text-xxs': 'span',
  'text-xxxs': 'span',
  'text-code-xs': 'code',
  'text-code-xxs': 'code',
  'text-code-xxxs': 'code',
  'para-md': 'p',
  'para-xmd': 'p',
  'para-sm': 'p',
  'para-xs': 'p',
};

const Typography = forwardRef<HTMLDivElement, React.PropsWithChildren<TypographyProps>>(
  (props, ref) => {
    const {
      variant,
      fontFace,
      className,
      children,
      align,
      weight,
      as,
      id,
      color,
      htmlFor,
      dangerouslySetInnerHTML,
      styleOverride,
      maxNumberOfLines,
      minNumberOfLines,
      style: propStyle,
      enableVisitedStyle,
      ...restProps
    } = props;

    /**
     * we can add new variants from theme manager which might not exist in the COMPONENTS object
     * so we fallback to span if the variant does not exist in the COMPONENTS object
     * or if the variant is not present in the props
     */
    const defaultComponent = (variant && COMPONENTS[variant]) ?? 'span';
    const Component = as ?? defaultComponent;
    const variantClassNames = slowCn(
      TYPOGRAPHY_ROOT_CLASSNAME,
      { 'visited:text-[var(--link-brand-fg)]': enableVisitedStyle },
      typographyVariants({
        variant,
        fontFace,
        align,
        weight,
        color,
        className,
      }),
    );

    const style = useMemo(() => {
      return getTypographyStyle({ maxNumberOfLines, minNumberOfLines, propStyle, styleOverride });
    }, [maxNumberOfLines, minNumberOfLines, propStyle, styleOverride]);

    // Typography is the single shared consumer of dangerouslySetInnerHTML across the app, so we
    // sanitize here rather than trusting every caller to do it (which several did not — the source
    // of stored-XSS via HTML bound over API/record/form data). DOMPurify strips <script>, event
    // handlers and javascript: URLs while keeping normal formatting, so rich-text callers are
    // unaffected. Trusted <style>/<script> injectors use raw elements, not Typography, so they
    // are out of this path. On the server DOMPurify has no DOM and passes through — matching the
    // pre-existing HtmlContent behaviour; the effective sanitisation happens on the client.
    const sanitizedInnerHTML = useMemo(() => {
      if (!dangerouslySetInnerHTML) return undefined;
      return { __html: DOMPurify.sanitize(dangerouslySetInnerHTML.__html) };
    }, [dangerouslySetInnerHTML]);

    return (
      <Component
        className={variantClassNames}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized via DOMPurify just above
        dangerouslySetInnerHTML={sanitizedInnerHTML}
        htmlFor={htmlFor}
        id={id}
        ref={ref as never}
        style={style}
        {...restProps}
        data-color={color}
        data-variant={variant}
        data-weight={weight}
      >
        {children}
      </Component>
    );
  },
);

Typography.displayName = 'Typography';

export default Typography;
